import { createContext, useContext, useEffect, useState } from "react";
import { useAuth } from "@/hooks/useAuth";
import { useQuery } from "@tanstack/react-query";
import type { Company } from "@shared/schema";

export type ThemeColor = "salefish" | "lavender" | "sky" | "mint" | "coral" | "peach" | "rose" | "sage" | "periwinkle" | "pewter" | "gold";

interface CompanyThemeContextType {
  themeColor: ThemeColor;
  isLoading: boolean;
}

const CompanyThemeContext = createContext<CompanyThemeContextType>({
  themeColor: "salefish",
  isLoading: true,
});

export const useCompanyTheme = () => useContext(CompanyThemeContext);

export const themeColorMap: Record<ThemeColor, { light: { hsl: string; rgb: string }, dark: { hsl: string; rgb: string } }> = {
  salefish: { 
    light: { hsl: "256 51% 36%", rgb: "69 45 140" },
    dark: { hsl: "252 44% 70%", rgb: "157 144 211" }
  },
  lavender: { 
    light: { hsl: "270 60% 50%", rgb: "128 51 204" },
    dark: { hsl: "240 23% 38%", rgb: "75 75 122" }
  },
  sky: { 
    light: { hsl: "200 80% 45%", rgb: "23 138 184" },
    dark: { hsl: "200 38% 38%", rgb: "58 107 130" }
  },
  mint: { 
    light: { hsl: "150 65% 42%", rgb: "38 178 128" },
    dark: { hsl: "150 25% 38%", rgb: "74 123 106" }
  },
  coral: { 
    light: { hsl: "15 75% 52%", rgb: "223 101 66" },
    dark: { hsl: "15 35% 38%", rgb: "160 69 64" }
  },
  peach: { 
    light: { hsl: "30 85% 50%", rgb: "237 142 24" },
    dark: { hsl: "30 42% 38%", rgb: "163 107 58" }
  },
  rose: { 
    light: { hsl: "340 70% 48%", rgb: "207 37 115" },
    dark: { hsl: "340 33% 38%", rgb: "160 75 106" }
  },
  sage: { 
    // 39%, not 40%. At L=40 this fill is 4.45:1 against white and 4.02:1
    // against the near-black alternative — it sits in the dead zone where
    // NEITHER foreground reaches WCAG AA 4.5:1, so no amount of picking the
    // better one fixes it. One percentage point darker takes it to 4.68:1.
    // `node scripts/theme-audit.mjs` is the check.
    light: { hsl: "120 30% 39%", rgb: "69 130 69" },
    dark: { hsl: "120 18% 38%", rgb: "86 109 86" }
  },
  periwinkle: { 
    light: { hsl: "225 65% 52%", rgb: "66 92 217" },
    dark: { hsl: "225 40% 52%", rgb: "85 85 170" }
  },
  pewter: { 
    light: { hsl: "0 0% 45%", rgb: "115 115 115" },
    dark: { hsl: "0 0% 58%", rgb: "130 130 130" }
  },
  gold: { 
    light: { hsl: "45 85% 45%", rgb: "212 167 18" },
    dark: { hsl: "45 53% 45%", rgb: "139 135 55" }
  },
};

// Map legacy colors to new pastel equivalents
const legacyColorMap: Record<string, ThemeColor> = {
  purple: "lavender",
  blue: "sky",
  green: "mint",
  red: "coral",
  orange: "peach",
  pink: "rose",
  teal: "sage",
  indigo: "periwinkle",
};

// Helper to migrate legacy colors to new palette
function migrateThemeColor(color: string | undefined): ThemeColor {
  if (!color) return "salefish";
  if (color in themeColorMap) return color as ThemeColor;
  if (color in legacyColorMap) return legacyColorMap[color];
  return "salefish";
}

// Helper to convert HEX to HSL for CSS variables
function hexToHSL(hex: string): string {
  // Remove # if present
  hex = hex.replace('#', '');
  
  // Convert to RGB
  const r = parseInt(hex.substring(0, 2), 16) / 255;
  const g = parseInt(hex.substring(2, 4), 16) / 255;
  const b = parseInt(hex.substring(4, 6), 16) / 255;
  
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0, s = 0;
  const l = (max + min) / 2;
  
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    
    switch (max) {
      case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
      case g: h = ((b - r) / d + 2) / 6; break;
      case b: h = ((r - g) / d + 4) / 6; break;
    }
  }
  
  h = Math.round(h * 360);
  s = Math.round(s * 100);
  const lPercent = Math.round(l * 100);
  
  return `${h} ${s}% ${lPercent}%`;
}

// Helper to convert HEX to RGB for CSS variables
function hexToRGB(hex: string): string {
  // Remove # if present
  hex = hex.replace('#', '');
  
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);
  
  return `${r} ${g} ${b}`;
}

/**
 * Black or white on this fill, whichever a reader can actually see.
 *
 * WHY THIS EXISTS
 * The provider used to set `--primary` and leave `--primary-foreground` at
 * whatever index.css declared — white in light mode, near-black (`0 0% 9%`) in
 * dark. Near-black is correct there ONLY because the default dark primary is a
 * light lavender at L=70%. Every other theme's dark value sits near L=38%, so
 * the button label was near-black text on a mid-dark fill.
 *
 * `node scripts/theme-audit.mjs` measured it: 14 of the 22 theme/mode
 * combinations were below WCAG AA 4.5:1 for button text, the worst at 2.20:1.
 * The default was one of the eight that passed, which is why it was never seen.
 *
 * The foreground has to be derived from the fill, not declared once — a tenant
 * picks their brand, and `custom:<hex>` means the space is unbounded, so no
 * fixed pair of tokens can be right for all of it.
 */
function readableForegroundHsl(primaryHsl: string): string {
  const [h, sPct, lPct] = primaryHsl.replace(/%/g, "").split(/\s+/).map(Number);
  if (!Number.isFinite(h) || !Number.isFinite(sPct) || !Number.isFinite(lPct)) {
    return "0 0% 100%";
  }
  const s = sPct / 100;
  const l = lPct / 100;
  const k = (n: number) => (n + h / 30) % 12;
  const a = s * Math.min(l, 1 - l);
  const ch = (n: number) => l - a * Math.max(-1, Math.min(k(n) - 3, Math.min(9 - k(n), 1)));
  const lin = (v: number) => (v <= 0.04045 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4);
  const lum = 0.2126 * lin(ch(0)) + 0.7152 * lin(ch(8)) + 0.0722 * lin(ch(4));

  /* Compare against the ACTUAL candidates, not against idealised white and
     black. The first version of this used `(lum + 0.05) / 0.05` for the dark
     option — the formula for PURE black — while returning `0 0% 9%`, whose real
     luminance is not zero. On `sage` that made 4.02:1 look like 4.71:1 and
     picked the worse of the two. */
  const LUM_WHITE = 1;                       // hsl(0 0% 100%)
  const LUM_NEAR_BLACK = lin(0.09);          // hsl(0 0% 9%), all channels equal
  const ratio = (a: number, b: number) =>
    (Math.max(a, b) + 0.05) / (Math.min(a, b) + 0.05);

  return ratio(lum, LUM_WHITE) >= ratio(lum, LUM_NEAR_BLACK) ? "0 0% 100%" : "0 0% 9%";
}

/**
 * The brand as TEXT: `--primary-ink`.
 *
 * `--primary` is a FILL and is solved for white-or-black on top of it. Used as
 * text on the page it is a different problem with a different threshold, and
 * one value cannot clear both: gold at 45% L carries near-black text at 6:1
 * and reads at ~2.5:1 as a link on cream. So the ink is derived from the fill
 * by walking lightness AWAY from the page — down in light mode, up in dark —
 * in one-point steps until it clears 4.5:1 against the page floor, keeping hue
 * and saturation so it still reads as the brand. Stops at the first value that
 * passes, so a fill that already reads (the default lavender does) is its own
 * ink and nothing changes.
 *
 * `scripts/theme-audit.mjs` runs the same walk for all eleven themes in both
 * modes and fails if any ink lands under 4.5.
 */
export function readableInkHsl(primaryHsl: string, isDark: boolean): string {
  const [h, sPct, lPct] = primaryHsl.replace(/%/g, "").split(/\s+/).map(Number);
  if (!Number.isFinite(h) || !Number.isFinite(sPct) || !Number.isFinite(lPct)) return primaryHsl;
  const lin = (v: number) => (v <= 0.04045 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4);
  const lum = (hh: number, ss: number, ll: number) => {
    const k = (n: number) => (n + hh / 30) % 12;
    const a = ss * Math.min(ll, 1 - ll);
    const ch = (n: number) => ll - a * Math.max(-1, Math.min(k(n) - 3, Math.min(9 - k(n), 1)));
    return 0.2126 * lin(ch(0)) + 0.7152 * lin(ch(8)) + 0.0722 * lin(ch(4));
  };
  // The page floors, as declared in index.css. Read as constants rather than
  // from the stylesheet because this runs per render and the floors are fixed.
  const page = isDark ? lum(252, 0.16, 0.095) : lum(252, 0.30, 0.95);
  const ratio = (a: number, b: number) => (Math.max(a, b) + 0.05) / (Math.min(a, b) + 0.05);
  const s = sPct / 100;
  let l = lPct / 100;
  const step = isDark ? 0.01 : -0.01;
  for (let i = 0; i < 100 && ratio(lum(h, s, l), page) < 4.5; i++) {
    l = Math.min(1, Math.max(0, l + step));
    if (l === 0 || l === 1) break;
  }
  return `${h} ${sPct}% ${Math.round(l * 100)}%`;
}

/**
 * The brand as text on a BRAND TINT: `--primary-deep`.
 *
 * `--primary-ink` is solved against the page, and a badge or a selected row is
 * not the page — it is `bg-primary/10–15`, a tint of the very hue the text is
 * in, and the ink loses contrast there (toward 4:1 on the default). This walks
 * on from the ink, away from the page, until it clears 5:1 on the brand at 15%
 * over the card, so a chip reads at every theme without a per-call override.
 */
export function readableDeepHsl(primaryHsl: string, isDark: boolean): string {
  const ink = readableInkHsl(primaryHsl, isDark);
  const [h, sPct, lPct] = ink.replace(/%/g, "").split(/\s+/).map(Number);
  const [fh, fs, fl] = primaryHsl.replace(/%/g, "").split(/\s+/).map(Number);
  if (![h, sPct, lPct, fh, fs, fl].every(Number.isFinite)) return ink;
  const rgb = (hh: number, ss: number, ll: number) => {
    const k = (n: number) => (n + hh / 30) % 12;
    const a = ss * Math.min(ll, 1 - ll);
    const ch = (n: number) => ll - a * Math.max(-1, Math.min(k(n) - 3, Math.min(9 - k(n), 1)));
    return [ch(0), ch(8), ch(4)];
  };
  const lin = (v: number) => (v <= 0.04045 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4);
  const lum = ([r, g, b]: number[]) => 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b);
  // The tint: the fill at 15% over the card, as declared in index.css.
  const card = isDark ? rgb(252, 0.14, 0.13) : rgb(252, 0.48, 0.972);
  const fill = rgb(fh, fs / 100, fl / 100);
  const tint = lum(fill.map((v, i) => v * 0.15 + card[i] * 0.85));
  const ratio = (a: number, b: number) => (Math.max(a, b) + 0.05) / (Math.min(a, b) + 0.05);
  const s = sPct / 100;
  let l = lPct / 100;
  const step = isDark ? 0.01 : -0.01;
  for (let i = 0; i < 100 && ratio(lum(rgb(h, s, l)), tint) < 5; i++) {
    l = Math.min(1, Math.max(0, l + step));
    if (l === 0 || l === 1) break;
  }
  return `${h} ${sPct}% ${Math.round(l * 100)}%`;
}

/**
 * The menu panel's colours, from the theme's own hue (Andrew, 2026-09-27: the
 * company theme drives the CTAs AND the menu colour).
 *
 * PropPay's menu is a deep maroon panel in both themes; SaleFish's is the same
 * deep panel in whichever hue the tenant picked. Lightness is FIXED — the panel
 * is always deep, so the light text ramp in index.css (--sidebar-foreground,
 * --sidebar-fg-*) reads on every theme — and only hue and a clamped saturation
 * follow the brand. A grey theme (pewter) gets a charcoal panel.
 */
export function menuPanelVars(primaryHsl: string, isDark: boolean): Record<string, string> {
  const [h, sPct] = primaryHsl.replace(/%/g, "").split(/\s+/).map(Number);
  if (!Number.isFinite(h) || !Number.isFinite(sPct)) return {};
  const sat = (max: number, min = 0) => Math.round(Math.min(max, Math.max(min, sPct)));
  return isDark
    ? {
        "--sidebar": `${h} ${sat(40)}% 13%`,
        "--sidebar-deep": `${h} ${sat(44)}% 8.5%`,
        "--sidebar-border": `${h} ${sat(30)}% 22%`,
        "--sidebar-accent": `${h} ${sat(28)}% 20%`,
        "--sidebar-on-active": `${h} ${sat(80)}% 82%`,
      }
    : {
        "--sidebar": `${h} ${sat(46)}% 17%`,
        "--sidebar-deep": `${h} ${sat(50)}% 11%`,
        "--sidebar-border": `${h} ${sat(34)}% 27%`,
        "--sidebar-accent": `${h} ${sat(32)}% 24%`,
        "--sidebar-on-active": `${h} ${sat(80)}% 82%`,
      };
}

/**
 * Where the resolved company colours are kept between launches, so
 * public/theme-init.js can put them on <html> before the first paint — the
 * app no longer opens in SaleFish violet and switches to the tenant's colour a
 * beat later. `{ v, light, dark }`, each a map of custom property → value.
 * Cleared on sign-out (AuthContext.logout) so the next person at the device
 * starts in the default. theme-init.js reads the same key and shape.
 */
export const COMPANY_THEME_CACHE_KEY = "salefish:company-theme";

type ThemeVars = Record<string, string>;

/** Every custom property the provider sets, for one mode. */
export function companyThemeVars(themeColor: ThemeColor, customColor: string | null, isDark: boolean): ThemeVars {
  if (customColor) {
    const hslValue = hexToHSL(customColor);
    return {
      "--primary": hslValue,
      "--primary-rgb": hexToRGB(customColor),
      "--primary-foreground": readableForegroundHsl(hslValue),
      "--primary-ink": readableInkHsl(hslValue, isDark),
      "--primary-deep": readableDeepHsl(hslValue, isDark),
      ...menuPanelVars(hslValue, isDark),
    };
  }
  const colorValues = themeColorMap[themeColor];
  if (!colorValues) return {};
  const values = isDark ? colorValues.dark : colorValues.light;
  // The FOREGROUND is derived from the fill — see readableForegroundHsl;
  // leaving it to the stylesheet put 14 of 22 theme/mode combinations below
  // WCAG AA. The INK is derived the same way for the other job the brand does
  // — see readableInkHsl. The menu takes the theme's hue from its LIGHT fill in
  // both modes: the dark fills are desaturated for page use and would grey the
  // panel.
  return {
    "--primary": values.hsl,
    "--primary-rgb": values.rgb,
    "--primary-foreground": readableForegroundHsl(values.hsl),
    "--primary-ink": readableInkHsl(values.hsl, isDark),
    "--primary-deep": readableDeepHsl(values.hsl, isDark),
    ...menuPanelVars(colorValues.light.hsl, isDark),
  };
}

function persistThemeVars(themeColor: ThemeColor, customColor: string | null) {
  try {
    if (!customColor && themeColor === "salefish") {
      // The default is what the stylesheet already says: nothing to pre-paint.
      window.localStorage.removeItem(COMPANY_THEME_CACHE_KEY);
      return;
    }
    window.localStorage.setItem(COMPANY_THEME_CACHE_KEY, JSON.stringify({
      v: 1,
      light: companyThemeVars(themeColor, customColor, false),
      dark: companyThemeVars(themeColor, customColor, true),
    }));
  } catch { /* private mode: the colours simply arrive a beat later */ }
}

export function CompanyThemeProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const [themeColor, setThemeColor] = useState<ThemeColor>("salefish");
  const [customColor, setCustomColor] = useState<string | null>(null);
  /* Until the colour is KNOWN, nothing is applied: theme-init.js has already
     painted the last-known colours (or the stylesheet's default), and writing
     the initial "salefish" over them while the company list loads was a flash
     of violet on every launch for a tenant with a theme of their own. */
  const [resolved, setResolved] = useState(false);

  const userTheme = (user as any)?.themeColor as string | undefined;
  const userCompanyId = (user as any)?.companyId as string | undefined;

  // The company list answers only "what is my company's colour?" — so it is
  // asked only when that is the question: a user with a colour of their own
  // (or no company) never needed the whole list on every cold launch.
  const { data: companies = [], isLoading, isFetched } = useQuery<Company[]>({
    queryKey: ['/api/companies'],
    enabled: !!userCompanyId && !userTheme,
  });

  useEffect(() => {
    // Priority: User theme > Company theme > Default salefish
    if (userTheme && userTheme.startsWith('custom:')) {
      // Custom color - extract the HEX value and store it
      setCustomColor(userTheme.substring(7));
      setThemeColor("salefish"); // Set a default but won't be used
      setResolved(true);
    } else if (userTheme) {
      // User has a personal theme preference - migrate if legacy
      setCustomColor(null);
      setThemeColor(migrateThemeColor(userTheme));
      setResolved(true);
    } else if (userCompanyId && companies.length > 0) {
      // No user theme, check company theme
      const userCompany = companies.find(c => c.id === userCompanyId);
      if ((userCompany as any)?.customColor) {
        setCustomColor((userCompany as any).customColor);
        setThemeColor("salefish"); // Set a default but won't be used
      } else if (userCompany?.themeColor) {
        setCustomColor(null);
        setThemeColor(migrateThemeColor(userCompany.themeColor));
      } else {
        setCustomColor(null);
        setThemeColor('salefish');
      }
      setResolved(true);
    } else if (!user || !userCompanyId || isFetched) {
      // Logged out, no company, or a company list that came back without one.
      setCustomColor(null);
      setThemeColor('salefish');
      setResolved(true);
    }
  }, [userTheme, userCompanyId, companies, user, isFetched]);

  // Apply theme color to CSS custom properties, detecting dark mode
  useEffect(() => {
    if (!resolved) return;
    persistThemeVars(themeColor, customColor);

    let printing = false;
    const applyColors = () => {
      const root = document.documentElement;
      // Detect if dark mode is active. Paper is always light: these values are
      // inline on <html>, so the stylesheet's `@media screen` scoping of the
      // dark palette cannot reach them — while printing, the light fill and
      // ink are applied instead, and put back after.
      const isDark = root.classList.contains('dark') && !printing;
      for (const [k, v] of Object.entries(companyThemeVars(themeColor, customColor, isDark))) {
        if (root.style.getPropertyValue(k) !== v) root.style.setProperty(k, v);
      }
    };

    // Apply colors immediately
    applyColors();

    const beforePrint = () => { printing = true; applyColors(); };
    const afterPrint = () => { printing = false; applyColors(); };
    window.addEventListener('beforeprint', beforePrint);
    window.addEventListener('afterprint', afterPrint);

    // Watch for theme changes (light/dark toggle)
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.attributeName === 'class') {
          applyColors();
        }
      });
    });

    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class']
    });

    return () => {
      observer.disconnect();
      window.removeEventListener('beforeprint', beforePrint);
      window.removeEventListener('afterprint', afterPrint);
    };
  }, [themeColor, customColor, resolved]);

  return (
    <CompanyThemeContext.Provider value={{ themeColor, isLoading }}>
      {children}
    </CompanyThemeContext.Provider>
  );
}
